import React from 'react'
import { Box, keyframes, styled } from '@mui/material'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../constants/interaxColors'

// Live dot colors - emerald green per design
const LIVE_GREEN = INTERAX_COLORS.green ?? '#10B981'
const LIVE_SOFT_BG = 'rgba(16, 185, 129, 0.08)'
const LIVE_BORDER = 'rgba(16, 185, 129, 0.3)'
const LIVE_DOT_GLOW = 'rgba(16, 185, 129, 0.6)'

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 ${LIVE_DOT_GLOW};
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
`

const StyledDotCore = styled(Box)({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  backgroundColor: LIVE_GREEN,
  position: 'relative',
  zIndex: 1,
  boxShadow: `0 0 8px ${LIVE_DOT_GLOW}`,
  animation: `${pulse} 1.5s infinite ease-in-out`
})

export interface LiveBadgeProps {
  /** Show "LIVE" label. Default: true for sidebar compact use */
  showLabel?: boolean
  /** Size variant */
  size?: 'small' | 'medium'
}

/**
 * LiveBadge - Compact badge with pulsing green dot for live activity indicator
 * Used in sidebar navigation when user has live activities
 */
export const LiveBadge: React.FC<LiveBadgeProps> = ({ showLabel = true, size = 'small' }) => {
  const { t } = useTranslation()

  const dotSize = size === 'small' ? 8 : 10

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.25,
        borderRadius: '100px',
        backgroundColor: LIVE_SOFT_BG,
        border: `1px solid ${LIVE_BORDER}`,
        flexShrink: 0
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: dotSize,
          height: dotSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <StyledDotCore />
      </Box>
      {showLabel && (
        <Box
          component="span"
          sx={{
            fontSize: size === 'small' ? '0.65rem' : '0.75rem',
            fontWeight: 800,
            letterSpacing: '0.05em',
            color: LIVE_GREEN
          }}
        >
          {t('common.live')}
        </Box>
      )}
    </Box>
  )
}

export default LiveBadge
